import { FC, useEffect, useState } from "react";
import {
  Button,
  Flex,
  InputNumber,
  LegacyForm,
  Typography,
  useMessagedAsyncFn,
  useMessagedAsyncRetry,
} from "@payping/web-components";
import { useAppTranslation } from "@/utils";
import { useDebounce } from "react-use";
import { URLS } from "@payping/utilities-shared/consts";
import { buildUrl } from "@payping/utilities-shared/url";
import { bnplConsumerApis } from "@payping/api";
import type { SDKRequestInit } from "@payping/api";
import Loading from "@/pages/loading";
import HowToWriteChequeDrawer from "@/Components/how-to-write-cheque-drawer";
import PayUnAvailable from "../../Unavailable";
import PayGetOrderChequeInquirySuccess from "./success";
import PayGetOrderLayout from "../Layout";
import PayGetOrderChequeInquiryFailed from "./failed";

interface ChequeInquiry {
  trackingCode: string;
  afterSuccess: () => void;
}

type InputsForm = {
  sayyadNumber: number;
};

const GetOrderChequeInquiry: FC<ChequeInquiry> = ({ afterSuccess, trackingCode }) => {
  const { t } = useAppTranslation();
  const [form] = LegacyForm.useForm<InputsForm>();
  const [sayyadValue, setSayyadValue] = useState("");
  const [openDrawer, setOpenDrawer] = useState(false);
  const [showFailAgain, setShowFailAgain] = useState({ show: false, tryCount: 0 });

  const [debouncedValue, setDebouncedValue] = useState("");
  useDebounce(() => setDebouncedValue(sayyadValue), 500, [sayyadValue]);

  const { loading, error, value, retry } = useMessagedAsyncRetry(
    async () => {
      const res = await bnplConsumerApis.getChequeInfo(trackingCode, { isRaw: true } as SDKRequestInit);

      return res;
    },
    [trackingCode],
    {
      success: false,
      error: true,
    },
  );

  const [chequeInquirySubmitState, chequeInquirySubmit] = useMessagedAsyncFn(
    async (sayyadNumber: string) => {
      try {
        const res = await bnplConsumerApis.chequeStatusInquiry({ trackingCode, sayyadNumber }, {
          isRaw: true,
        } as SDKRequestInit);

        if (res.success === false) {
          setShowFailAgain((pre) => ({
            ...pre,
            show: true,
          }));
        } else {
          setShowFailAgain((pre) => ({
            ...pre,
            show: false,
          }));
        }

        return res;
      } catch (e: any) {
        if (e.status === 400) {
          try {
            const parsedError = await e.json();
            throw parsedError;
          } catch (jsonError: any) {
            if (jsonError.status === 400) {
              throw jsonError;
            }
            throw e;
          }
        }
        throw e;
      }
    },
    [trackingCode],
  );

  useEffect(() => {
    if (debouncedValue) {
      form.validateFields(["sayyadNumber"]);
    }
  }, [debouncedValue, form]);

  const handleFinishForm = async (data: InputsForm) => {
    await chequeInquirySubmit(data.sayyadNumber.toString());
  };

  if (loading || error?.name === "AuthError" || chequeInquirySubmitState?.error?.name === "AuthError") {
    return (
      <PayGetOrderLayout showHeaders={false}>
        <Loading />
      </PayGetOrderLayout>
    );
  }

  if (error) {
    return (
      <PayGetOrderLayout showHeaders={false}>
        <PayUnAvailable
          onRetry={() => {
            retry();
          }}
          onRetryLoading={loading}
        />
      </PayGetOrderLayout>
    );
  }

  if (chequeInquirySubmitState.error || (showFailAgain.show && showFailAgain.tryCount >= 3)) {
    return (
      <PayGetOrderLayout showHeaders currentStepError>
        <PayGetOrderChequeInquiryFailed message={t("pay.getOrder.steps.chequeInquiry.unAvailableContent")} />
      </PayGetOrderLayout>
    );
  }

  if (chequeInquirySubmitState.value?.success === true && value) {
    return (
      <PayGetOrderLayout showHeaders>
        <PayGetOrderChequeInquirySuccess data={value} afterSuccess={afterSuccess} />
      </PayGetOrderLayout>
    );
  }

  return (
    <PayGetOrderLayout showHeaders currentStepError={showFailAgain.show}>
      {showFailAgain.show ? (
        <PayGetOrderChequeInquiryFailed
          message={chequeInquirySubmitState?.value?.message ?? t("pay.getOrder.steps.chequeInquiry.unAvailableContent")}
          onRetry={() => {
            setShowFailAgain((pre) => ({
              show: false,
              tryCount: chequeInquirySubmitState?.value?.retry === false ? pre.tryCount + 1 : pre.tryCount,
            }));
          }}
        />
      ) : (
        <Flex vertical className="tw:relative tw:h-full tw:w-full tw:px-4! tw:pb-4! tw:pt-8!" gap="large">
          <Flex gap="small" vertical>
            <Flex align="center" justify="space-between">
              <Typography.Title level={5} className="tw:m-0! tw:font-semibold">
                {t("pay.getOrder.steps.chequeInquiry.title")}
              </Typography.Title>
              <Button
                className="tw:m-0! tw:p-0!"
                type="link"
                onClick={() => {
                  setOpenDrawer(true);
                }}
              >
                {t("howToWriteCheckDrawer.title")}
              </Button>
            </Flex>
          </Flex>

          <img
            src={buildUrl(URLS.STORAGE_URL, "bnpl-consumer/sayyad-number-cheque.png").toString()}
            className="tw:h-auto tw:w-full tw:object-contain"
            alt={"sayyad-number-cheque"}
          />

          <LegacyForm form={form} layout="vertical" onFinish={handleFinishForm}>
            <LegacyForm.Item<InputsForm>
              name="sayyadNumber"
              label={t("pay.getOrder.steps.chequeInquiry.form.sayyadNumber.label")}
              rules={[
                { required: true },
                {
                  validator(_, val) {
                    const v = String(val || "").trim();

                    if (!v) return Promise.resolve();

                    if (!/^\d+$/.test(v)) {
                      return Promise.reject(new Error(t("formValidate.sayyadNumber")));
                    }

                    if (v.length > 16) {
                      return Promise.reject(new Error(t("formValidate.sayyadNumber")));
                    }

                    if (v.length < 16) {
                      if (v !== debouncedValue) return Promise.resolve();
                      return Promise.reject(new Error(t("formValidate.sayyadNumber")));
                    }

                    return Promise.resolve();
                  },
                },
              ]}
            >
              <InputNumber
                placeholder={t("pay.getOrder.steps.chequeInquiry.form.sayyadNumber.placeholder")}
                onChange={(val) => setSayyadValue(String(val ?? ""))}
                inputMode="numeric"
                changeOnWheel={false}
                controls={false}
                className="tw:w-full!"
              />
            </LegacyForm.Item>
          </LegacyForm>

          <Button
            block
            loading={chequeInquirySubmitState.loading}
            htmlType="submit"
            type="primary"
            className="tw:mt-auto"
            onClick={() => {
              form.submit();
            }}
          >
            {t("pay.getOrder.steps.chequeInquiry.title")}
          </Button>
        </Flex>
      )}

      {value ? (
        <HowToWriteChequeDrawer
          open={openDrawer}
          onClose={() => {
            setOpenDrawer(false);
          }}
          data={{
            amountRial: value?.amountRial,
            duedate: value?.duedate,
            recipientIdentifier: value?.recipientIdentifier,
            recipientName: value?.recipientName,
            isLegal: value?.recipientIsLegal ?? false,
          }}
        />
      ) : null}
    </PayGetOrderLayout>
  );
};

export default GetOrderChequeInquiry;
